import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SolidButton, LinerButton } from '../atoms';
import { UserApiClient } from '../../services/userApi';

interface PasswordExpiryNotificationProps {
  open: boolean;
  onClose: () => void;
}

export const PasswordExpiryNotification: React.FC<PasswordExpiryNotificationProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isExtending, setIsExtending] = useState(false);

  // 비밀번호 변경하러 가기
  const handleChangePassword = () => {
    onClose();
    navigate('/settings?openPasswordDialog=true&reason=expired');
  };

  // 6개월 연장
  const handleExtendPassword = async () => {
    try {
      setIsExtending(true);
      const result = await UserApiClient.extendPassword();
      console.log('✅ [PasswordExpiry] 비밀번호 연장 성공:', result);
      onClose();
    } catch (error) {
      console.error('❌ [PasswordExpiry] 비밀번호 연장 실패:', error);
      // 실패 시에도 다이얼로그 닫기
      onClose();
    } finally {
      setIsExtending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: '1px solid #c4c4c4',
          padding: '16px',
          minWidth: '320px',
          maxWidth: '400px',
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          {/* Warning Icon - Alter.svg */}
          <Box
            sx={{
              flexShrink: 0,
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/images/Alter.svg"
              alt="Warning"
              style={{ width: '34px', height: '30px' }}
            />
          </Box>

          {/* Message Text */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '40px',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Noto Sans KR', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '20px',
                color: '#0e0e0e',
                mb: 1,
              }}
            >
              비밀번호 만료 안내
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Noto Sans KR', sans-serif",
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '20px',
                color: '#0e0e0e',
                whiteSpace: 'pre-wrap',
              }}
            >
              비밀번호를 설정한지 90일이 지났습니다.{'\n'}
              보안을 위해 비밀번호를 변경해주세요.
            </Typography>

            {/* 액션 버튼 */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <LinerButton
                onClick={handleExtendPassword}
                disabled={isExtending}
                sx={{ fontSize: '12px', minWidth: '100px' }}
              >
                {isExtending ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
                6개월 연장
              </LinerButton>
              <SolidButton
                onClick={handleChangePassword}
                disabled={isExtending}
                sx={{ fontSize: '12px', minWidth: '100px' }}
              >
                비밀번호 변경
              </SolidButton>
            </Box>
          </Box>

          {/* Close Button - Close.svg */}
          <Box
            onClick={onClose}
            sx={{
              flexShrink: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            <img
              src="/assets/images/Close.svg"
              alt="Close"
              style={{ width: '24px', height: '24px' }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
